/**
 * Number Inventory Page
 *
 * Lists every phone number on the Telnyx account and lets a Super Admin assign
 * one to one or more tenants. Numbers may be:
 *   - unimported (only on Telnyx, no local PhoneNumber row yet)
 *   - dedicated (1 tenant assignment)
 *   - shared (2+ tenant assignments — true sharing via the join table)
 *
 * 10DLC campaign attachment is intentionally NOT done here; that flow lives on
 * each account's Phone Numbers tab.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, RefreshCw, AlertCircle, Loader2, Filter } from 'lucide-react';
import { Button, Card, CardContent } from '@respectly/ui';
import { useNumberInventoryStore, type InventoryItem } from '../../stores/useNumberInventoryStore';
import { NumberInventoryTable } from '../../components/inventory/NumberInventoryTable';
import { InventoryFilters } from '../../components/inventory/InventoryFilters';
import { AssignToAccountsDialog } from '../../components/inventory/AssignToAccountsDialog';

export function NumberInventory() {
  const {
    items,
    isLoading,
    loadError,
    filters,
    isAssigning,
    isUnassigning,
    actionError,
    setFilter,
    load,
    assignToTenants,
    unassign,
    setDefault,
  } = useNumberInventoryStore();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [singleAssignTarget, setSingleAssignTarget] = useState<InventoryItem | null>(null);

  useEffect(() => {
    load();
  }, [load]);

  // Re-fetch whenever filters change.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.capability, filters.type]);

  const counts = useMemo(() => {
    let unassigned = 0;
    let dedicated = 0;
    let shared = 0;
    for (const i of items) {
      if (i.assignments.length === 0) unassigned++;
      else if (i.assignments.length === 1) dedicated++;
      else shared++;
    }
    return { total: items.length, unassigned, dedicated, shared };
  }, [items]);

  const selectedItems = useMemo(
    () => items.filter((i) => selectedIds.has(i.telnyx.id)),
    [items, selectedIds],
  );

  const toggleSelect = (telnyxId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(telnyxId)) next.delete(telnyxId);
      else next.add(telnyxId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.telnyx.id)));
    }
  };

  const handleBulkAssignClick = () => {
    if (selectedIds.size === 0) return;
    setSingleAssignTarget(null);
    setAssignDialogOpen(true);
  };

  const handleAssignFor = (item: InventoryItem) => {
    setSingleAssignTarget(item);
    setAssignDialogOpen(true);
  };

  const handleAssignSubmit = async (params: {
    tenantIds: string[];
    setDefaultForTenantId?: string | null;
  }) => {
    const targets = singleAssignTarget ? [singleAssignTarget] : selectedItems;
    let allOk = true;
    for (const t of targets) {
      const ok = await assignToTenants({
        telnyx: t.telnyx,
        monthlyFee: t.local?.monthlyFee,
        tenantIds: params.tenantIds,
        setDefaultForTenantId: params.setDefaultForTenantId ?? null,
      });
      if (!ok) allOk = false;
    }
    if (allOk) {
      setAssignDialogOpen(false);
      setSingleAssignTarget(null);
      setSelectedIds(new Set());
    }
    return allOk;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="space-y-5"
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Super admin
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold text-slate-900 dark:text-white">
            <Phone className="h-6 w-6 text-slate-400" />
            Number Inventory
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Every phone number on the Telnyx account, and the tenants currently using each.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load()}
          disabled={isLoading}
          className="border-slate-300 dark:border-slate-700"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Counter label="Total" value={counts.total} />
        <Counter label="Unassigned" value={counts.unassigned} tone="amber" />
        <Counter label="Dedicated" value={counts.dedicated} tone="sky" />
        <Counter label="Shared" value={counts.shared} tone="violet" />
      </div>

      {/* Errors */}
      {loadError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-300">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">Couldn&apos;t load inventory</p>
              <p className="mt-0.5 text-xs">{loadError.message}</p>
            </div>
          </div>
        </div>
      )}
      {actionError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-300">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">Action failed</p>
              <p className="mt-0.5 text-xs">{actionError.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters + bulk actions */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <InventoryFilters filters={filters} onChange={(k, v) => setFilter(k, v)} />
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <span className="text-xs text-slate-500">
                {selectedIds.size} selected
              </span>
            )}
            <Button
              size="sm"
              onClick={handleBulkAssignClick}
              disabled={selectedIds.size === 0 || isAssigning}
              className="bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              {isAssigning ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Phone className="mr-1.5 h-3.5 w-3.5" />
              )}
              Assign to accounts…
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <NumberInventoryTable
        items={items}
        isLoading={isLoading}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onAssign={handleAssignFor}
        onUnassign={(phoneNumberId, tenantId) => unassign(phoneNumberId, tenantId)}
        onSetDefault={(phoneNumberId, tenantId, isDefault) =>
          setDefault(phoneNumberId, tenantId, isDefault)
        }
        isUnassigning={isUnassigning}
      />

      {/* Assign dialog */}
      <AssignToAccountsDialog
        open={assignDialogOpen}
        onClose={() => {
          setAssignDialogOpen(false);
          setSingleAssignTarget(null);
        }}
        targets={singleAssignTarget ? [singleAssignTarget] : selectedItems}
        excludeTenantIds={
          singleAssignTarget
            ? new Set(singleAssignTarget.assignments.map((a) => a.tenantId))
            : new Set()
        }
        isSubmitting={isAssigning}
        onSubmit={handleAssignSubmit}
      />
    </motion.div>
  );
}

function Counter({
  label,
  value,
  tone = 'slate',
}: {
  label: string;
  value: number;
  tone?: 'slate' | 'amber' | 'sky' | 'violet';
}) {
  const toneClass = {
    slate: 'text-slate-700 dark:text-slate-200',
    amber: 'text-amber-700 dark:text-amber-300',
    sky: 'text-sky-700 dark:text-sky-300',
    violet: 'text-violet-700 dark:text-violet-300',
  }[tone];
  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardContent className="p-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
        <p className={`mt-1 text-2xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
